<template>
  <v-container fluid class="company-page">
    <!-- Alert Banner -->
    <AAlertBanner variant="danger" :messages="alertError" title="Warning" :icon-size="22" />

    <!-- Header -->
    <div class="company-header">
      <div class="company-header__top">
        <div class="company-header__info" v-if="!loadingDialog">
          <h1 class="company-header__title">{{ model.name.plural }}</h1>
        </div>
        <div class="company-header__badge">
          <v-icon size="16" color="var(--app-accent)" class="mr-1">mdi-chart-arc</v-icon>
          <span class="company-header__progress">{{ totalProgress }}</span>
        </div>
      </div>
    </div>

    <!-- Action Buttons Row -->
    <div class="company-actions">
      <button
        class="action-btn action-btn--outlined-gray"
        @click="ssmAuthLoginDialog = true"
      >
        <v-icon size="16" class="mr-1">mdi-key-variant</v-icon>
        Get SSM Auth Token API
      </button>
      <div class="company-actions__spacer"></div>
      <button
        class="action-btn action-btn--blue"
        @click="getHrdcCompanies()"
      >
        <v-icon size="16" color="#fff" class="mr-1">mdi-domain</v-icon>
        1. Get HRDC Companies
      </button>
      <button
        class="action-btn action-btn--outlined-amber"
        @click="getSsmCompanyData('NEW_BRM')"
      >
        <v-icon size="16" class="mr-1" color="#d97706">mdi-api</v-icon>
        2A. Call SSM API (New BRM)
      </button>
      <button
        class="action-btn action-btn--outlined-teal"
        @click="getSsmCompanyData('OLD_BRM')"
      >
        <v-icon size="16" class="mr-1" color="#0d9488">mdi-api</v-icon>
        2B. Call SSM API (Old BRM)
      </button>
    </div>

    <!-- Spreadsheet Table -->
    <div class="sheet-wrap">
      <div class="sheet-bar" v-if="datas.length > 0">
        <span class="sheet-bar__count">{{ datas.length }} companies</span>
      </div>
      <div class="sheet-scroll" v-if="datas.length > 0">
        <table class="sheet">
          <thead>
            <tr>
              <th class="sheet__th sheet__th--idx">#</th>
              <th class="sheet__th">HRDC Company</th>
              <th class="sheet__th">HRDC BRM</th>
              <th class="sheet__th">SSM Company Name</th>
              <th class="sheet__th">New BRM</th>
              <th class="sheet__th">Old BRM</th>
              <th class="sheet__th sheet__th--act"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in datas" :key="index" class="sheet__row">
              <td class="sheet__td sheet__td--idx">{{ item.idx }}</td>
              <td class="sheet__td">
                <button class="sheet-pill" @click="copyContent(datas[index].oldCompanyName)">{{ datas[index].oldCompanyName }}</button>
              </td>
              <td class="sheet__td">
                <button class="sheet-pill sheet-pill--purple" @click="copyContent(datas[index].oldSsmNo)">{{ datas[index].oldSsmNo }}</button>
              </td>
              <td class="sheet__td">
                <v-text-field dense outlined v-model="datas[index].ssmCompanyName" hide-details placeholder="—" class="sheet-input"></v-text-field>
              </td>
              <td class="sheet__td">
                <v-text-field dense outlined v-model="datas[index].ssmNewBrm" hide-details placeholder="—" class="sheet-input"></v-text-field>
              </td>
              <td class="sheet__td">
                <v-text-field dense outlined v-model="datas[index].ssmOldBrm" hide-details placeholder="—" class="sheet-input"></v-text-field>
              </td>
              <td class="sheet__td sheet__td--act">
                <button class="sheet-save" @click="updateCompanyData(index)">
                  <v-icon size="14" color="#fff">mdi-check</v-icon>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="datas.length === 0" class="table-empty">
      <v-icon size="48" color="#cbd5e1">mdi-office-building-remove-outline</v-icon>
      <span class="table-empty__text">No company records found</span>
      <span class="table-empty__sub">Click "1. Get HRDC Companies" to load data</span>
    </div>

    <HrdcLoader :loadingDialog="loadingDialog"></HrdcLoader>

    <!-- SSM Auth Login Dialog -->
    <v-dialog v-model="ssmAuthLoginDialog" max-width="520" content-class="confirm-dialog">
      <div class="confirm-card">
        <h3 class="confirm-card__title">SSM Login</h3>
        <p class="confirm-card__message">Enter your SSM credentials to authenticate.</p>

        <AError :api="apiGetSsmAuthToken"></AError>

        <div class="ssm-login-field">
          <div style="display: flex; align-items: center; gap: 12px;">
            <label class="ssm-login-field__label" style="flex-shrink: 0; min-width: 60px; margin-bottom: 0;">Token</label>
            <v-text-field
              dense
              outlined
              v-model="ssmLoginToken"
              placeholder="Enter token"
              hide-details
              class="company-input"
            >
            </v-text-field>
          </div>
        </div>
        <div class="ssm-login-field">
          <div style="display: flex; align-items: center; gap: 12px;">
            <label class="ssm-login-field__label" style="flex-shrink: 0; min-width: 60px; margin-bottom: 0;">Cookie</label>
            <v-text-field
              dense
              outlined
              v-model="ssmCookie"
              placeholder="Enter cookie"
              hide-details
              class="company-input"
            >
            </v-text-field>
          </div>
        </div>

        <div class="confirm-card__actions" style="margin-top: 24px;">
          <button class="confirm-card__btn confirm-card__btn--cancel" @click="ssmAuthLoginDialog = false">
            Cancel
          </button>
          <button class="confirm-card__btn confirm-card__btn--confirm" @click="getSsmAuthToken()">
            Get Token
          </button>
        </div>
      </div>
    </v-dialog>
  </v-container>
</template>

<script>
import Api from "@/objects/api";
import { mapState } from "vuex";
import Model from "@/objects/model";
import Service from "@/objects/service";

export default {
  computed: {
    ...mapState({
      auth: (state) => state.auth.data,
    }),
    totalProgress() {
      return `(${this.finishedCompaniesLength}/${this.allCompaniesLength} - ${(
        (this.finishedCompaniesLength / this.allCompaniesLength) *
        100
      ).toFixed(2)}%)`;
    },
  },
  props: ["params"],
  data: () => ({
    apiGetCompanies: new Api(),
    apiGetSsmAuthToken: new Api(),
    apiUpdateCompanyData: new Api(),
    model: new Model(),
    service: new Service(),
    alertError: null,
    loadingDialog: false,
    datas: [],
    SSM_EINFO_SEARCH_API_URL: "https://www.ssm-einfo.my/api/v1/search",
    SSM_AUTH_TOKEN: null,
    ssmAuthLoginDialog: false,
    ssmLoginToken: null,
    ssmCookie: null,
    allCompaniesLength: 0,
    finishedCompaniesLength: 0,
  }),
  async created() {
    this.showLoadingDialog();
    this.model.getByKey(this.params.modelKey);
    if (this.model.serviceKey) this.service.set("key", this.model.serviceKey);

    // Assign apiDetails by mapping repository.
    const apiDetails = {
      apiGetCompanies: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/company/get-all-companies`,
      },
      apiGetSsmAuthToken: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/company/get-ssm-login-token`,
        method: "post",
      },
      apiUpdateCompanyData: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/company/update-company-data`,
        method: "post",
      },
    };

    for (const api in apiDetails) {
      if (apiDetails[api].url !== undefined) {
        this[api].setUrl(apiDetails[api].url);
      }
      if (apiDetails[api].method !== undefined) {
        this[api].setMethod(apiDetails[api].method);
      }
      if (apiDetails[api].query !== undefined) {
        this[api].setQuery(apiDetails[api].query);
      }
      if (apiDetails[api].params !== undefined) {
        this[api].setParams(apiDetails[api].params);
      }
    }

    this.rolesAssignation(this.auth.roles);

    this.SSM_AUTH_TOKEN = this.$store.state.hrdc.ssmToken;

    // Get leftover company progress.
    this.apiGetCompanies.setQuery(`all=true`);
    this.apiGetCompanies.setCallbackCompleted((response) => {
      const { allCompaniesLength, finishedCompaniesLength } = response;
      this.allCompaniesLength = allCompaniesLength;
      this.finishedCompaniesLength = finishedCompaniesLength;
    });
    this.apiGetCompanies.fetch();

    this.hideLoadingDialog();
  },
  methods: {
    redirectBack() {
      let route = "HrdcSettings";
      if (this.params.backTo) {
        route = this.params.backTo;
      }

      this.$router.push({
        name: route,
        params: {
          modelKey: this.model.key,
          backTo: "",
        },
      });
    },
    getHrdcCompanies() {
      this.showLoadingDialog();
      this.alertError = null;

      this.apiGetCompanies.setQuery("");
      this.apiGetCompanies.setCallbackCompleted((response) => {
        this.datas = [];

        const data = response.data;

        for (let i = 0; i < data.length; i++) {
          this.datas.push({
            idx: i + 1,
            uuid: data[i].uuid,
            oldCompanyName: data[i].oldCompanyName.toUpperCase(),
            oldSsmNo: data[i].oldSsmNo.trim(),
            oldFormerSsmNo: null,
            ssmCompanyName: null,
            ssmNewBrm: null,
            ssmOldBrm: null,
          });
        }

        this.hideLoadingDialog();
      });
      this.apiGetCompanies.fetch();
    },
    getSsmCompanyData(ssmType) {
      this.alertError = null;

      if (this.datas.length == 0) {
        this.alertError = "Please retrieve company data first";
      }

      for (let i = 0; i < this.datas.length; i++) {
        let keyword = null;
        let tmpKeywordArr = null;

        if (ssmType == "NEW_BRM") {
          tmpKeywordArr = this.datas[i].oldSsmNo.split("(");
          keyword = tmpKeywordArr[0];
        }

        if (ssmType == "OLD_BRM") {
          let ssmNo = this.datas[i].oldSsmNo;
          tmpKeywordArr = ssmNo.slice(0, -1) + "-" + ssmNo.slice(-1);
          keyword = tmpKeywordArr;
        }

        this.$axios
          .post(
            this.SSM_EINFO_SEARCH_API_URL,
            {
              keyword,
              "g-recaptcha-response": 1,
              stype: 1,
              page: 1,
              searchControl: "on",
              // _token: "JWc1tyiwjjHtM6dueTm3wcA4STIUdFtRbNc3KmhF",
            },
            {
              headers: {
                Authorization: `Bearer ${this.SSM_AUTH_TOKEN}`,
              },
            }
          )
          .then((res) => {
            let data = res.data.data.data;
            if (data.length > 0) {
              data = data[0];

              const org_name = data.org_name;
              const oldSsmNo = data.new_no;
              const oldFormerSsmNo = `${data.old_no}-${data.AlphaCode}`;

              this.datas[i].ssmCompanyName = org_name;
              this.datas[i].ssmNewBrm = oldSsmNo;
              this.datas[i].ssmOldBrm = oldFormerSsmNo;
            }
          })
          .catch((err) => {
            if (err.response.status == 401) {
              this.alertError = "Authorization token expired, please refresh!";
            }

            return false;
          });
      }
    },
    getSsmAuthToken() {
      this.apiGetSsmAuthToken.setParams({
        _token: this.ssmLoginToken,
        form_name: "login",
        username: "alanwake88",
        password: "Abcde@12345",
        cookie: this.ssmCookie,
      });
      this.apiGetSsmAuthToken.setCallbackCompleted((response) => {
        try {
          this.$store.commit("assignSsmAuthToken", response);
          this.SSM_AUTH_TOKEN = response;

          this.ssmAuthLoginDialog = false;
        } catch (err) {
          console.log(err);
        }
      });

      this.apiGetSsmAuthToken.fetch();
    },
    copyContent(item) {
      const textarea = document.createElement("textarea");
      textarea.value = item.trim();
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");

      // Remove the temporary textarea
      document.body.removeChild(textarea);

      this.$store.dispatch("showMessage", {
        message: "Copied Successfully!",
        messageType: "info",
        timeout: 2000,
      });
    },
    updateCompanyData(index) {
      const data = this.datas[index];

      if (!data.ssmCompanyName || !data.ssmNewBrm || !data.ssmOldBrm) {
        this.alertError = "Please fill in all the required fields!";
        return false;
      }

      this.apiUpdateCompanyData.setParams(data);
      this.apiUpdateCompanyData.setCallbackCompleted((response) => {
        if (response) {
          this.datas = this.datas.filter((item) => item.idx !== data.idx);
          this.finishedCompaniesLength++;
        }
      });
      this.apiUpdateCompanyData.fetch();
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .company-page {
  background-color: var(--app-page);
}

.dark-mode .company-header {
  background: var(--app-elevated);
}

.dark-mode .back-btn {
  background: var(--app-card);
  border: 1px solid var(--app-border-strong);
}

.dark-mode .back-btn:hover {
  background: var(--app-surface);
  border-color: var(--app-border-strong);
}

.dark-mode .company-header__badge {
  background: var(--app-accent-soft);
}

.dark-mode .action-btn--outlined-gray {
  background: var(--app-card);
  color: var(--app-neutral-fg);
  border: 1.5px solid var(--app-border-strong);
}

.dark-mode .action-btn--outlined-gray:hover {
  background: var(--app-surface);
  border-color: var(--app-border-strong);
}

.dark-mode .action-btn--outlined-amber {
  background: var(--app-warning-bg);
}

.dark-mode .action-btn--outlined-amber:hover {
  background: var(--app-warning-bg);
}

.dark-mode .section-card {
  background: var(--app-elevated);
}

.dark-mode .section-body {
  background: var(--app-card);
}

.dark-mode .sheet-wrap {
  background: var(--app-card);
  border: 1px solid var(--app-border);
}

.dark-mode .sheet-bar {
  background: var(--app-surface);
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .sheet-bar__count {
  color: var(--app-text-faint);
}

.dark-mode .sheet__th {
  color: var(--app-text-faint);
  background: var(--app-surface);
  border-bottom: 2px solid var(--app-border);
}

.dark-mode .sheet__row {
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .sheet__td {
  color: var(--app-text);
}

.dark-mode .sheet__td--idx {
  color: var(--app-text-faint);
}

.dark-mode .sheet-pill {
  background: var(--app-accent-soft);
}

.dark-mode .sheet-pill:hover {
  background: var(--app-accent-soft);
}

.dark-mode .sheet-pill--purple {
  background: var(--app-purple-bg);
}

.dark-mode .sheet-pill--purple:hover {
  background: var(--app-purple-bg);
}

.dark-mode .table-empty__text {
  color: var(--app-text-faint);
}

.dark-mode .table-empty__sub {
  color: var(--app-text-faint);
}

.dark-mode .ssm-login-card {
  background: var(--app-card);
}

.dark-mode .ssm-login-field__label {
  color: var(--app-neutral-fg);
}

.dark-mode .ssm-login-card__btn--cancel {
  background: var(--app-card);
  color: var(--app-neutral-fg);
  border: 1.5px solid var(--app-border-strong);
}

.dark-mode .ssm-login-card__btn--cancel:hover {
  background: var(--app-surface);
  border-color: var(--app-border-strong);
}

/* >>> end auto-dark <<< */

/* ============================================
   PAGE WRAPPER
   ============================================ */
.company-page {
  background-color: #d8dce2;
  /* Fills main minus the breadcrumb and footer; a `100vh - 64px` guess only
     subtracts the app bar and overflows. See .layout-main__content. */
  flex: 1 0 auto;
  padding-top: 24px !important;
  padding-bottom: 60px !important;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ============================================
   HEADER
   ============================================ */
.company-header {
  background: #eaecef;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  margin-bottom: 24px;
  overflow: hidden;
}

.company-header__top {
  display: flex;
  align-items: center;
  padding: 24px 28px;
}

.back-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #d1d5db;
  cursor: pointer;
  margin-right: 16px;
  transition: all 0.2s ease;
}

.back-btn:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.company-header__info {
  flex: 1;
  min-width: 0;
}

.company-header__title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #3a3f47;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  letter-spacing: -0.2px;
}

.company-header__badge {
  display: inline-flex;
  align-items: center;
  background: #e0f2fe;
  border: 1px solid #bae6fd;
  border-radius: 20px;
  padding: 6px 14px;
  flex-shrink: 0;
}

.company-header__progress {
  font-size: 0.82rem;
  font-weight: 700;
  color: #0369a1;
  letter-spacing: -0.2px;
}

/* ============================================
   ACTION BUTTONS ROW
   ============================================ */
.company-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 24px;
}

.company-actions__spacer {
  flex: 1;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  padding: 9px 18px;
  border-radius: 10px;
  font-family: 'Lato', sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.action-btn--outlined-gray {
  background: #fff;
  color: #475569;
  border: 1.5px solid #cbd5e1;
}

.action-btn--outlined-gray:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.action-btn--blue {
  background: linear-gradient(135deg, #2563eb, #3b82f6);
  color: #fff;
  border: none;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.25);
}

.action-btn--blue:hover {
  background: linear-gradient(135deg, #1d4ed8, #2563eb);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.35);
}

.action-btn--outlined-amber {
  background: #fffbeb;
  color: #b45309;
  border: 1.5px solid #fbbf24;
}

.action-btn--outlined-amber:hover {
  background: #fef3c7;
  border-color: #f59e0b;
}

.action-btn--outlined-teal {
  background: #f0fdfa;
  color: #0f766e;
  border: 1.5px solid #5eead4;
}

.action-btn--outlined-teal:hover {
  background: #ccfbf1;
  border-color: #2dd4bf;
}

/* ============================================
   SECTION CARDS
   ============================================ */
.section-card {
  background: #eaecef;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
  margin-bottom: 24px;
}

.section-card:hover {
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.06);
}

.section-header {
  background: linear-gradient(135deg, #0ea5e9, #38bdf8);
  padding: 13px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.section-header__title {
  color: #ffffff;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.section-header__count {
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.72rem;
  font-weight: 500;
}

.section-body {
  background: #fff;
}

/* ============================================
   SPREADSHEET TABLE
   ============================================ */
.sheet-wrap {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.sheet-bar {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.sheet-bar__count {
  font-size: 0.75rem;
  font-weight: 600;
  color: #94a3b8;
}

.sheet-scroll {
  overflow-x: auto;
}

.sheet {
  width: 100%;
  border-collapse: collapse;
  font-family: 'Lato', sans-serif;
}

.sheet__th {
  padding: 10px 10px;
  font-size: 0.68rem;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: left;
  background: #f8fafc;
  border-bottom: 2px solid #e2e8f0;
  white-space: nowrap;
}

.sheet__th--idx { width: 44px; text-align: center; }
.sheet__th--act { width: 44px; }

.sheet__row {
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.1s ease;
}

.sheet__row:hover { background: #fafbfc; }

.sheet__td {
  padding: 6px 8px;
  font-size: 0.82rem;
  color: #1e293b;
  vertical-align: middle;
}

.sheet__td--idx {
  text-align: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: #94a3b8;
  width: 44px;
}

.sheet__td--act { width: 44px; text-align: center; }

.sheet-pill {
  display: inline-block;
  background: #f0f9ff;
  border: none;
  border-radius: 4px;
  padding: 3px 8px;
  font-family: 'Lato', sans-serif;
  font-size: 0.78rem;
  font-weight: 600;
  color: #0369a1;
  cursor: pointer;
  transition: background 0.12s;
  text-align: left;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sheet-pill:hover { background: #e0f2fe; }

.sheet-pill--purple { background: #f5f3ff; color: #7c3aed; }
.sheet-pill--purple:hover { background: #ede9fe; }

.sheet-save {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: #10b981;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}

.sheet-save:hover { background: #059669; }

/* Update Button */
.update-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #2563eb, #3b82f6);
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 7px 18px;
  font-family: 'Lato', sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.2);
}

.update-btn:hover {
  background: linear-gradient(135deg, #1d4ed8, #2563eb);
  box-shadow: 0 4px 10px rgba(37, 99, 235, 0.35);
}

/* Empty State */
.table-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px 0;
}

.table-empty__text {
  font-size: 0.88rem;
  color: #94a3b8;
  font-weight: 500;
}

.table-empty__sub {
  font-size: 0.78rem;
  color: #cbd5e1;
  font-weight: 400;
}

/* ============================================
   SSM LOGIN DIALOG
   ============================================ */
.ssm-login-card {
  background: #fff;
  border-radius: 20px;
  overflow: hidden;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
}

.ssm-login-card__header {
  background: linear-gradient(135deg, #0ea5e9, #38bdf8);
  padding: 18px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.ssm-login-card__header-content {
  display: flex;
  align-items: center;
}

.ssm-login-card__header-title {
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.2px;
}

.ssm-login-card__close {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;
}

.ssm-login-card__close:hover {
  background: rgba(255, 255, 255, 0.3);
}

.ssm-login-card__body {
  padding: 24px 24px 16px;
}

.ssm-login-field {
  margin-bottom: 16px;
}

.ssm-login-field__label {
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  color: #475569;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.ssm-login-card__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 0 24px 24px;
}

.ssm-login-card__btn {
  padding: 10px 22px;
  border-radius: 10px;
  font-family: 'Lato', sans-serif;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
}

.ssm-login-card__btn--cancel {
  background: #fff;
  color: #475569;
  border: 1.5px solid #cbd5e1;
}

.ssm-login-card__btn--cancel:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.ssm-login-card__btn--confirm {
  background: linear-gradient(135deg, #2563eb, #3b82f6);
  color: #fff;
  border: none;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.25);
}

.ssm-login-card__btn--confirm:hover {
  background: linear-gradient(135deg, #1d4ed8, #2563eb);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.35);
}

/* ============================================
   TRANSITIONS
   ============================================ */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 959px) {
  .company-header__top {
    padding: 16px 20px;
  }

  .company-actions {
    gap: 8px;
  }

  .company-actions__spacer {
    display: none;
  }

  .action-btn {
    font-size: 0.75rem;
    padding: 8px 14px;
  }

  .company-table__header,
  .company-table__row {
    flex-wrap: wrap;
    gap: 8px;
  }

  .company-table__col--no {
    width: 50px;
  }

  .company-table__col--name,
  .company-table__col--brm,
  .company-table__col--hrdc {
    min-width: 100%;
    flex: unset;
  }
}

/* ============================================
   DARK MODE
   ============================================ */
.dark-mode .company-page { background-color: var(--app-page); }
.dark-mode .company-header { background: var(--app-card); }
.dark-mode .company-header__title { color: var(--app-text); }
.dark-mode .company-header__badge { background: var(--app-accent-soft); border-color: var(--app-accent-border); }
.dark-mode .company-header__progress { color: var(--app-accent-strong); }
.dark-mode .back-btn { background: var(--app-page); border-color: var(--app-border); }
.dark-mode .back-btn:hover { background: var(--app-card); }
.dark-mode .action-btn--outlined-gray { background: var(--app-card); color: var(--app-neutral-fg); border-color: var(--app-border); }
.dark-mode .action-btn--outlined-amber { background: var(--app-card); color: var(--app-warning-fg); border-color: var(--app-warning-fg); }
.dark-mode .action-btn--outlined-teal { background: var(--app-card); color: var(--app-success-fg); border-color: var(--app-teal-border); }
.dark-mode .section-card { background: var(--app-card); }
.dark-mode .section-body { background: var(--app-card); }
.dark-mode .company-table__header { background: var(--app-page); border-color: var(--app-border); }
.dark-mode .company-table__header .company-table__col { color: var(--app-text-muted); }
.dark-mode .company-table__row { border-color: var(--app-border); }
.dark-mode .company-table__row:hover { background: var(--app-border); }
.dark-mode .company-table__col { color: var(--app-text); }
.dark-mode .table-index { background: var(--app-border); color: var(--app-text-muted); }
.dark-mode .detail-val { background: var(--app-border); border-color: var(--app-border-strong); color: var(--app-text); }
.dark-mode .detail-val:hover { background: var(--app-accent-soft); border-color: var(--app-accent); color: var(--app-accent-strong); }
.dark-mode .hrdc-data-label { color: var(--app-text-faint); }
.dark-mode .ssm-login-card { background: var(--app-card); }
.dark-mode .ssm-login-card__body { color: var(--app-text); }
.dark-mode .ssm-login-field__label { color: var(--app-text-muted); }
.dark-mode .ssm-login-card__btn--cancel { background: var(--app-page); color: var(--app-neutral-fg); border-color: var(--app-border); }
</style>

<style>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .sheet-input.v-text-field--outlined fieldset {
  border-color: var(--app-border) !important;
}

.dark-mode .sheet-input.v-text-field--outlined:hover fieldset {
  border-color: var(--app-border-strong) !important;
}

.dark-mode .sheet-input input {
  color: var(--app-text) !important;
}

.dark-mode .sheet-input input::placeholder {
  color: var(--app-text-faint) !important;
}

/* >>> end auto-dark <<< */

/* SSM Login Dialog (unscoped - renders outside component) */
.ssm-login-dialog {
  border-radius: 20px !important;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12) !important;
}

/* Sheet inline inputs */
.sheet-input.v-text-field--outlined fieldset {
  border-color: #e2e8f0 !important;
  border-radius: 6px !important;
}

.sheet-input.v-text-field--outlined:hover fieldset {
  border-color: #cbd5e1 !important;
}

.sheet-input.v-input--is-focused fieldset {
  border-color: #0ea5e9 !important;
  border-width: 1.5px !important;
}

.sheet-input .v-input__slot {
  min-height: 32px !important;
  padding: 0 8px !important;
}

.sheet-input input {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.82rem !important;
  font-weight: 600 !important;
  color: #1e293b !important;
  padding: 4px 0 !important;
}

.sheet-input input::placeholder {
  color: #cbd5e1 !important;
  font-weight: 400 !important;
}
</style>
